import parsePhoneNumber from "libphonenumber-js";
import dayjs, { Dayjs } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

export const formatCurrency = (value: string, showArrowWithValue?: boolean, showBracketsWithValue?: boolean) => {
  if (value === "") return "";

  if (isNaN(Number(value))) {
    if (showBracketsWithValue) {
      return `(${value})`;
    }
    return `${value}`;
  }

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
  });

  if (Number(value) === 0) {
    if (showBracketsWithValue) {
      return "($0)";
    }
    return "$0";
  }
  let formattedValue = formatter.format(Number(value)).replace("$", "").replace(".00", "");
  if (formattedValue.startsWith("-")) {
    formattedValue = formattedValue.replace("-", "");
    if (showBracketsWithValue) {
      return showArrowWithValue ? `↓ ($${formattedValue})` : `(-$${formattedValue})`;
    }
    return showArrowWithValue ? `↓ $${formattedValue}` : `-$${formattedValue}`;
  } else {
    if (showBracketsWithValue) {
      return showArrowWithValue ? `↑ ($${formattedValue})` : `($${formattedValue})`;
    }
    return showArrowWithValue ? `↑ $${formattedValue}` : `$${formattedValue}`;
  }
};

export const removeHttpFromUrl = (url: string) => {
  if (!url) return "";

  if (url.trim().startsWith("http")) {
    return url.replace("http://", "").replace("https://", "");
  }

  return url;
};

export const formatUrl = (url: string) => {
  if (!url) return "";

  if (url?.startsWith("http")) {
    return url;
  }

  return `https://${url}`;
};

export const formatDecimal = (number: number | string) => {
  const toFixed = Number(number) % 1 > 0 ? 2 : 0;

  return Number.parseFloat(number as string)
    .toFixed(toFixed)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const formatPhoneNumber = (phoneNumberString = "") => {
  if (!phoneNumberString) return "";

  const phoneNumber = parsePhoneNumber(phoneNumberString, "US");
  return phoneNumber?.formatNational() || null;
};

export enum DateFormat {
  DEFAULT = "MMM D YYYY, h:mm a",
  DEFAULT_WITHOUT_TIME = "MMM D, YYYY",
  DEFAULT_WITHOUT_YEAR = "MMM D, h:mm a",
  DEFAULT_WITHOUT_YEAR_TIME = "MMM D"
}

const getDateFormat = (value: string | Date, currentTime: Dayjs, showTime = false) => {
  return dayjs(value).isSame(currentTime, "year")
    ? showTime
      ? DateFormat.DEFAULT_WITHOUT_YEAR
      : DateFormat.DEFAULT_WITHOUT_YEAR_TIME
    : showTime
      ? DateFormat.DEFAULT
      : DateFormat.DEFAULT_WITHOUT_TIME;
};

export const formatDateTime = (value?: string | Date, showTime = false, showRelativeTime = false) => {
  if (!value) {
    return "";
  }
  const currentTime = dayjs();
  const dateFormat = getDateFormat(value, currentTime, showTime);

  if (showTime && showRelativeTime) {
    return dayjs(value).fromNow();
  }

  if (showTime) {
    return dayjs(value).format(dateFormat);
  }

  if (dayjs(value).isAfter(currentTime)) {
    return dayjs(value).format(dateFormat);
  }

  if (dayjs(value).isAfter(dayjs().subtract(1, "minute"))) {
    return "a few seconds ago";
  }

  if (dayjs(value).isAfter(dayjs().subtract(1, "hour"))) {
    return `${currentTime.diff(dayjs(value), "minute")}m ago`;
  }
  if (dayjs(value).isAfter(dayjs().subtract(24, "hour"))) {
    return `${currentTime.diff(dayjs(value), "hour")}h ago`;
  }
  if (dayjs(value).isAfter(dayjs().subtract(1, "week"))) {
    return `${currentTime.diff(dayjs(value), "day")}d ago`;
  }
  return dayjs(value).format(dateFormat);
};

export const formatDateFull = (value: string) => {
  return dayjs(value).format(DateFormat.DEFAULT);
};

export const formatDate = (value: string) => {
  return dayjs(value).format(DateFormat.DEFAULT_WITHOUT_TIME);
};

export const formatAddress = (address: {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
}) => {
  const { address1: addressLine1, address2: addressLine2, city, state, zip: zipCode } = address;

  let finalAddress = addressLine1 ? addressLine1 : "";

  if (addressLine2) {
    finalAddress = finalAddress ? `${finalAddress}, ${addressLine2}` : addressLine2;
  }

  if (city && finalAddress) {
    finalAddress += `, ${city}`;
  } else if (city) {
    finalAddress += city;
  }

  if (finalAddress && (state || zipCode)) {
    finalAddress += `, ${state || ""} ${zipCode || ""}`;
  } else if (state || zipCode) {
    finalAddress += `${state || ""} ${zipCode || ""}`;
  }

  return finalAddress;
};

// removes  params added by supabase with #
export const getSanitizedRedirectUrl = (url?: string | null) => {
  if (!url) return;
  return url.split("#")[0];
};

export const formatPercentage = (value: string, showArrowWithValue?: boolean, showBracketsWithValue?: boolean) => {
  if (value === "") return "";

  if (isNaN(Number(value))) {
    if (showBracketsWithValue) {
      return `(${value})`;
    }
    return `${value}`;
  }

  const formatter = new Intl.NumberFormat("en-US", {
    style: "percent",
    maximumFractionDigits: 2
  });
  if (Number(value) === 0) {
    if (showBracketsWithValue) {
      return "(0%)";
    }
    return "0%";
  }
  let formattedValue = formatter.format(Number(value) / 100);
  if (formattedValue.startsWith("-")) {
    formattedValue = formattedValue.replace("-", "");
    if (showBracketsWithValue) {
      return showArrowWithValue ? `↓ (${formattedValue})` : `(-${formattedValue})`;
    }
    return showArrowWithValue ? `↓ ${formattedValue}` : `-${formattedValue}`;
  } else {
    if (showBracketsWithValue) {
      return showArrowWithValue ? `↑ (${formattedValue})` : `(${formattedValue})`;
    }
    return showArrowWithValue ? `↑ ${formattedValue}` : formattedValue;
  }
};

export const formatNumber = (
  value?: number | string,
  showArrowWithValue?: boolean,
  showBracketsWithValue?: boolean
): string => {
  if (value === "") return "";
  if (isNaN(Number(value))) {
    if (showBracketsWithValue) {
      return `(${value})`;
    }
    return `${value}`;
  }

  const numValue = Number(value);
  if (numValue === 0) {
    if (showBracketsWithValue) {
      return "(0)";
    }
    return "0";
  }
  const formattedValue = numValue.toLocaleString();
  if (formattedValue.startsWith("-")) {
    formattedValue.replace("-", "");
    if (showBracketsWithValue) {
      return showArrowWithValue ? `↓ (${formattedValue})` : `(-${formattedValue})`;
    }
    return showArrowWithValue ? `↓ ${formattedValue}` : `-${formattedValue}`;
  } else {
    if (showBracketsWithValue) {
      return showArrowWithValue ? `↑ (${formattedValue})` : `(${formattedValue})`;
    }
    return showArrowWithValue ? `↑ ${formattedValue}` : formattedValue;
  }
};
